/**
 * WF-Input - Base text input component for wizard forms
 *
 * A standalone text input with validation, styling, and form integration.
 *
 * @example
 * <wf-input name="fullName" label="Full Name" required placeholder="John Doe" />
 */

import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { FormInputBase } from './base/form-input-base.js';
import { ValidationController } from '../controllers/ValidationController.js';

@customElement('wf-input')
export class WfInput extends FormInputBase {

  // ============================================
  // Input-specific Properties
  // ============================================

  /**
   * Placeholder text
   */
  @property({ type: String })
  placeholder = '';

  /**
   * Input type (text, tel, url, etc.)
   */
  @property({ type: String })
  type: 'text' | 'tel' | 'url' | 'search' = 'text';

  /**
   * Minimum length
   */
  @property({ type: Number })
  minlength?: number;

  /**
   * Maximum length
   */
  @property({ type: Number })
  maxlength?: number;

  /**
   * Pattern regex string
   */
  @property({ type: String })
  pattern?: string;

  /**
   * Pattern validation message
   */
  @property({ type: String, attribute: 'pattern-message' })
  patternMessage = 'Invalid format';

  /**
   * Whether field is disabled
   */
  @property({ type: Boolean })
  disabled = false;

  /**
   * Autocomplete attribute
   */
  @property({ type: String })
  autocomplete = '';

  // ============================================
  // Abstract Method Implementations
  // ============================================

  protected _getValidationTriggerProperties(): string[] {
    return ['required', 'minlength', 'maxlength', 'pattern'];
  }

  protected _setupValidators(): void {
    this._validators = [];

    if (this.required) {
      this._validators.push(ValidationController.required());
    }

    if (this.minlength !== undefined) {
      this._validators.push(ValidationController.minLength(this.minlength));
    }

    if (this.maxlength !== undefined) {
      this._validators.push(ValidationController.maxLength(this.maxlength));
    }

    if (this.pattern) {
      this._validators.push(
        ValidationController.pattern(new RegExp(this.pattern), this.patternMessage)
      );
    }
  }

  // ============================================
  // Render
  // ============================================

  render() {
    return html`
      <div class="wf-field-container">
        ${this._renderLabel()}
        <input
          type="${this.type}"
          class="wf-input ${this._errorMessage ? 'wf-input-error' : ''}"
          id="${this.name}"
          name="${this.name}"
          placeholder="${this.placeholder}"
          .value="${this._value}"
          ?disabled="${this.disabled}"
          ?required="${this.required}"
          autocomplete="${this.autocomplete || 'off'}"
          data-testid="wf-input"
          @input="${this._handleInput}"
          @blur="${this._handleBlur}"
          @focus="${this._handleFocus}"
        />
        ${this._renderHint()}
        ${this._renderError()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-input': WfInput;
  }
}
